/**
 * Sinnbilder der Oberflaeche.
 *
 * Eigene SVG statt einer Bibliothek: Es sind zwei Dutzend, sie sind klein, und
 * eine Abhaengigkeit fuer wenige Kilobyte einzuhandeln lohnt nicht. Ausserdem
 * laesst sich so der Strich einheitlich halten.
 *
 * Alle auf einem Raster von 24 mal 24, Strichstaerke 1.6, ohne Fuellung. Die
 * Farbe kommt von aussen ueber currentColor - so nehmen sie die Farbe des
 * umgebenden Textes an und passen sich Zustaenden an, ohne dass es eine eigene
 * Fassung je Farbe braucht.
 */

/**
 * Exportiert (Paket U3), damit `navigation/struktur.ts` je Eintrag direkt eine
 * Icon-Komponente referenzieren kann, ohne eine zweite Namensliste zu fuehren.
 */
export type IconProps = {
  /** Kantenlaenge in Pixeln. Vorgabe 18 - passt zur Zeilenhoehe der Oberflaeche. */
  size?: number;
  /** Ueberschreibt currentColor, etwa fuer einen Zustand. */
  color?: string;
  className?: string;
  title?: string;
};

function Svg({
  size = 18, color, className, title, children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      style={{ flexShrink: 0, verticalAlign: 'middle' }}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

// --- Navigation ---------------------------------------------------------------

/** Zustand: ein Zeigerinstrument. */
export const IconZustand = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 20a8 8 0 1 0-8-8" />
    <path d="M12 12l4-3" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
  </Svg>
);

/** Auftraege: gestapelte Blaetter. */
export const IconAuftrag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 3h8l3 3v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
    <path d="M16 3v4h3" />
    <path d="M9 12h6M9 16h4" />
  </Svg>
);

/** Projekte: ein Gebaeude. */
export const IconProjekt = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 21V6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v15" />
    <path d="M13 10h6a1 1 0 0 1 1 1v10" />
    <path d="M3 21h18" />
    <path d="M7 9h2M7 13h2M7 17h2M16 14h1M16 17h1" />
  </Svg>
);

/** Maschinen: ein Servergehaeuse. */
export const IconMaschine = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="7" rx="1.5" />
    <rect x="3" y="13" width="18" height="7" rx="1.5" />
    <path d="M7 7.5h.01M7 16.5h.01" />
    <path d="M11 7.5h5M11 16.5h5" />
  </Svg>
);

/** Installation: ein Pfeil in eine Ablage. */
export const IconInstallation = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v10" />
    <path d="M8.5 9.5 12 13l3.5-3.5" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </Svg>
);

// --- Raumbuch -------------------------------------------------------------------

/** Geschoss: gestapelte Ebenen von der Seite. */
export const IconEbene = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 3 7.5l9 4.5 9-4.5L12 3Z" />
    <path d="M3 12.5 12 17l9-4.5" />
    <path d="M3 17.5 12 22l9-4.5" />
  </Svg>
);

/** Tür: ein Türblatt mit Griff. */
export const IconTuer = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" />
    <path d="M4 21h16" />
    <circle cx="14.5" cy="12" r="1" fill="currentColor" stroke="none" />
  </Svg>
);

/** Türtyp: ein Türblatt mit Etikett. */
export const IconTuertyp = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 21V4a1 1 0 0 1 1-1h7l4 4v14" />
    <path d="M4 21h16M14 3v4h4" />
    <path d="M9 13h5M9 16h3" />
  </Svg>
);

/** Raum: ein Grundriss mit Tuer. */
export const IconRaum = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4h16v16H4z" />
    <path d="M4 14v6" strokeWidth={2.4} />
    <path d="M9 20H4" strokeWidth={0} />
    <path d="M4 14a5 5 0 0 1 5 5" strokeDasharray="1.5 1.8" />
  </Svg>
);

/** Heizung: ein Heizkoerper. */
export const IconHeizung = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="7" width="16" height="11" rx="1.5" />
    <path d="M8 7v11M12 7v11M16 7v11" />
    <path d="M6 4c0 1.2 1 1.2 1 2.4M11 4c0 1.2 1 1.2 1 2.4M16 4c0 1.2 1 1.2 1 2.4" />
  </Svg>
);

/** Kuehlung: eine Schneeflocke. */
export const IconKuehlung = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5 4.2 16.5" />
    <path d="M12 6.5 9.8 4.8M12 6.5l2.2-1.7M12 17.5l-2.2 1.7M12 17.5l2.2 1.7" />
  </Svg>
);

/** Lueftung: ein Luefterrad. */
export const IconLueftung = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="1.8" />
    <path d="M12 10.2c0-3 1-5.2 3-5.2 1.6 0 2.4 1.4 1.6 3-.7 1.4-2.4 2.2-4.6 2.2" />
    <path d="M13.6 12.9c2.6-1.5 4.9-1.9 5.9-.2.8 1.4-.1 2.8-1.9 2.9-1.6.1-3.2-1-4-2.7" />
    <path d="M10.4 12.9c-2.6-1.5-4.9-1.9-5.9-.2-.8 1.4.1 2.8 1.9 2.9 1.6.1 3.2-1 4-2.7" />
  </Svg>
);

/** Elektro: ein Blitz. */
export const IconElektro = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13 2 5 13h6l-2 9 9-12h-6l1-8Z" />
  </Svg>
);

/** Sanitaer: ein Wassertropfen. */
export const IconSanitaer = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3s6 6.4 6 10.4a6 6 0 0 1-12 0C6 9.4 12 3 12 3Z" />
    <path d="M9.5 14a2.5 2.5 0 0 0 2.5 2.5" />
  </Svg>
);

/** Sicherheit: ein Schild. */
export const IconSicherheit = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 5 6v6c0 4.2 2.9 7.7 7 9 4.1-1.3 7-4.8 7-9V6l-7-3Z" />
    <path d="m9.5 12 1.8 1.8 3.4-3.6" />
  </Svg>
);

/** Nutzung: zwei Personen. */
export const IconNutzung = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5" />
    <path d="M17 14.5a5.5 5.5 0 0 1 3.5 5.5" />
  </Svg>
);

/** Kennung: ein Etikett. */
export const IconKennung = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 11.6V5a2 2 0 0 1 2-2h6.6a2 2 0 0 1 1.4.6l7 7a2 2 0 0 1 0 2.8l-6.6 6.6a2 2 0 0 1-2.8 0l-7-7a2 2 0 0 1-.6-1.4Z" />
    <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
  </Svg>
);

// --- Zustaende und Handlungen -----------------------------------------------------

export const IconOk = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.4 2.4 4.6-5" />
  </Svg>
);

export const IconWarnung = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.3 3.9 2.5 17.4A2 2 0 0 0 4.2 20.5h15.6a2 2 0 0 0 1.7-3.1L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4.5M12 17h.01" />
  </Svg>
);

export const IconFehler = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </Svg>
);

export const IconLaeuft = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.2 2" />
  </Svg>
);

export const IconHerunterladen = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v11" />
    <path d="m8 10.5 4 4 4-4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </Svg>
);

export const IconHochladen = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 15V4" />
    <path d="m8 7.5 4-4 4 4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </Svg>
);

export const IconTabelle = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" rx="1.5" />
    <path d="M3 9h18M3 14.5h18M9 9v11M15 9v11" />
  </Svg>
);

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const IconSchluessel = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="8" cy="15" r="4" />
    <path d="m10.8 12.2 8.2-8.2" />
    <path d="m17 6 2.5 2.5M14.5 8.5 17 11" />
  </Svg>
);

export const IconModell = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 2.5 8.5 4.8v9.4L12 21.5 3.5 16.7V7.3L12 2.5Z" />
    <path d="m3.5 7.3 8.5 4.8 8.5-4.8M12 12.1v9.4" />
  </Svg>
);

export const IconDatei = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9l-6-6Z" />
    <path d="M13 3v6h6" />
  </Svg>
);

export const IconLage = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
);

export const IconSuche = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 4 4" />
  </Svg>
);

/** Raumbuch als Dokument: ein Blatt mit Zeilen und Siegel. */
export const IconBuch = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 12h7M8.5 15h7M8.5 18h4" />
  </Svg>
);

/** Hilfe: ein aufgeschlagenes Buch. */
export const IconHilfe = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 6.5C10.5 5 8.5 4.5 6 4.5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2c2.5 0 4.5.5 6 2" />
    <path d="M12 6.5c1.5-1.5 3.5-2 6-2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2c-2.5 0-4.5.5-6 2" />
    <path d="M12 6.5v14" />
  </Svg>
);

/** Zeitplan: ein Kalenderblatt mit Uhr. */
export const IconZeitplan = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 5h-14a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
    <path d="M3 10h18M8 3v4M16 3v4" />
    <circle cx="18" cy="17" r="4" />
    <path d="M18 15.3V17l1.2.8" />
  </Svg>
);

// --- Exportarten ------------------------------------------------------------

/** IFC: ein Wuerfel mit Datenpunkt - das Modell als Datenmodell. */
export const IconIfc = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 2.5 8 4.6v9.2l-8 4.6-8-4.6V7.1l8-4.6Z" />
    <path d="m4 7.1 8 4.6 8-4.6M12 11.7v9.2" />
    <circle cx="12" cy="7" r="1.3" fill="currentColor" stroke="none" />
  </Svg>
);

/** PDF: ein Blatt mit Knick. */
export const IconPdf = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 13.5h1.2a1.2 1.2 0 0 1 0 2.4H8.5v-2.4Zm0 2.4V18" />
    <path d="M12.8 13.5v4.5h1a1.5 1.5 0 0 0 1.5-1.5v-1.5a1.5 1.5 0 0 0-1.5-1.5h-1Z" />
  </Svg>
);

/** DWG: ein Zeichenwinkel. */
export const IconDwg = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20 20 4" />
    <path d="M4 20h6M4 20v-6" />
    <path d="M4 14 14 4h6v6" />
    <path d="M8 16.5h1.5M11 13.5h1.5M14 10.5h1.5" />
  </Svg>
);

/** Raumdaten lesen: ein Pfeil aus dem Modell heraus. */
export const IconRaumLesen = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4h9v7" />
    <path d="M4 4v16h9" />
    <path d="M4 14a5 5 0 0 1 5 5" strokeDasharray="1.5 1.8" />
    <path d="M21 12h-6" />
    <path d="m17.5 8.5-3.5 3.5 3.5 3.5" />
  </Svg>
);

/** Raumdaten schreiben: ein Pfeil ins Modell hinein. */
export const IconRaumSchreiben = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4h9v7" />
    <path d="M4 4v16h9" />
    <path d="M4 14a5 5 0 0 1 5 5" strokeDasharray="1.5 1.8" />
    <path d="M15 12h6" />
    <path d="m17.5 8.5 3.5 3.5-3.5 3.5" />
  </Svg>
);

/**
 * Ordnet einer Fachgruppe des Raumbuchs ihr Sinnbild zu.
 *
 * Die Gruppennamen kommen aus der Datenbank; kommt eine unbekannte dazu, gibt
 * es eben kein Bild - besser als ein falsches.
 */
export function GruppenIcon({ gruppe, size = 16 }: { gruppe: string; size?: number }) {
  switch (gruppe) {
    case 'Kennung':    return <IconKennung size={size} />;
    case 'Heizung':    return <IconHeizung size={size} />;
    case 'Kühlung':   return <IconKuehlung size={size} />;
    case 'Lüftung':   return <IconLueftung size={size} />;
    case 'Elektro':    return <IconElektro size={size} />;
    case 'Sanitär':   return <IconSanitaer size={size} />;
    case 'Sicherheit': return <IconSicherheit size={size} />;
    case 'Nutzung':    return <IconNutzung size={size} />;
    default:           return null;
  }
}

/* === Baugruppen einer Zentrale ==========================================
 *
 * Acht Sinnbilder fuer die Bestueckung. Sie muessen auf 26 Pixeln unterscheidbar
 * bleiben - deshalb keine Details, sondern je eine kennzeichnende Grundform:
 *
 *   Ringmodul       ein geschlossener Ring
 *   Netzwerkmodul   drei verbundene Punkte
 *   Netzteil        eine Steckdose mit Stift
 *   Akku            ein Kasten mit Ladestand
 *   Bedienfeld      ein Bildschirm mit Tasten
 *   Ansteuerung     zwei Pfeile in beide Richtungen
 *   Gehaeuse        ein Schrank mit Feldern
 *   Sonstiges       ein Kaestchen
 *
 * Der Ring ist der einzige, der eine Aussage traegt: Er ist geschlossen, weil eine
 * Ringleitung beide Enden an der Zentrale hat. Ein offener Bogen wuerde einen Stich
 * zeigen, und der ist etwas anderes.
 */

/** Ringmodul - der geschlossene Ring einer Ringleitung. */
export function IconRingmodul(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="5" r="1.6" />
    </Svg>
  );
}

/** Netzwerkmodul - drei verbundene Teilnehmer. */
export function IconNetzwerkmodul(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="5" r="2" />
      <circle cx="5" cy="18" r="2" />
      <circle cx="19" cy="18" r="2" />
      <path d="M10.5 6.7 6.3 16M13.5 6.7 17.7 16M7 18h10" />
    </Svg>
  );
}

/** Netzteil - Anschluss mit Stift. */
export function IconNetzteil(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="7" width="16" height="12" rx="2" />
      <path d="M9 4v3M15 4v3M9 12h6" />
    </Svg>
  );
}

/** Akku - Kasten mit Ladestand. */
export function IconAkku(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="8" width="16" height="9" rx="1.5" />
      <path d="M21 11v3" />
      <path d="M6 11v3M9.5 11v3M13 11v3" />
    </Svg>
  );
}

/** Bedienfeld - Bildschirm mit Tasten. */
export function IconBedienfeld(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M7 19h10" />
      <circle cx="8" cy="10" r="1.2" />
      <circle cx="12" cy="10" r="1.2" />
      <circle cx="16" cy="10" r="1.2" />
    </Svg>
  );
}

/** Ansteuerung - Signal in beide Richtungen. */
export function IconAnsteuerung(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 9h16M16 5.5 20 9l-4 3.5" />
      <path d="M20 16H4M8 12.5 4 16l4 3.5" />
    </Svg>
  );
}

/** Gehaeuse - Schrank mit Feldern. */
export function IconGehaeuse(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M4 9h16M4 15h16M15.5 6h1.5M15.5 12h1.5M15.5 18h1.5" />
    </Svg>
  );
}

/** Sonstiges - ein Kaestchen. */
export function IconBaugruppe(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="6" width="16" height="12" rx="1.5" />
      <path d="M8 10h8M8 14h5" />
    </Svg>
  );
}

/* === Navigationsgruppen (Paket U3) =======================================
 *
 * Acht Sinnbilder fuer die Gruppen der linken Hauptnavigation
 * (docs/AUFTRAG-2026-09-17-ux-modernisierung.md, §4). PROJEKT und
 * DOKUMENTATION verwenden bestehende Sinnbilder (IconProjekt, IconBuch) -
 * fuer die uebrigen sechs Gruppen gab es noch kein passendes Symbol.
 */

/** Fachplanung - Stift, der eine Linie zieht. */
export function IconFachplanung(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M14.5 4.5 19.5 9.5 9 20H4v-5Z" />
      <path d="M12.5 6.5l5 5" />
    </Svg>
  );
}

/** Berechnungen - Sigma. */
export function IconBerechnung(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M17 5H7l5 7-5 7h10" />
    </Svg>
  );
}

/** Koordination - zwei ueberlappende Koerper. */
export function IconKoordination(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="6.5" width="11" height="11" rx="1.5" />
      <rect x="9.5" y="10.5" width="11" height="11" rx="1.5" />
    </Svg>
  );
}

/** KI - ein Funke/Stern. */
export function IconKi(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
      <circle cx="12" cy="12" r="2.4" />
    </Svg>
  );
}

/** Standards - Massstab mit Haken. */
export function IconStandard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="4" width="17" height="7" rx="1" />
      <path d="M7 4v3M10.5 4v3M14 4v3M17.5 4v3" />
      <path d="M6 16l3 3 8-9" />
    </Svg>
  );
}

/** Verwaltung - ein Zahnrad. */
export function IconVerwaltung(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.5v2.4M12 18.1v2.4M20.5 12h-2.4M5.9 12H3.5" />
      <path d="M17.8 6.2l-1.7 1.7M7.9 16.1l-1.7 1.7M17.8 17.8l-1.7-1.7M7.9 7.9 6.2 6.2" />
    </Svg>
  );
}

/** Seitenleiste einklappen/ausklappen - ein Doppelpfeil. */
export function IconEinklappen(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M9 6 4 12l5 6" />
      <path d="M16 6l-5 6 5 6" />
    </Svg>
  );
}
